import React, { useState } from "react";

import { corTextoSobre, gradienteHero } from "../../theme/cores";
import { MarcaAbertura } from "./cacheAbertura";

// Tela de abertura do app (carregamento inicial da Início).
// Ocupa a tela inteira, por cima da barra de abas: é desenhada direto no <body> (portal), porque dentro da tela da
// navegação o zIndex só vale dentro dela e a barra de abas (bottom tabs) ficava por cima, visível e clicável.
// - Sem marca em cache (1ª visita) ou empresa do modo legado: abertura padrão AG.SIM.
// - Com marca em cache: abertura com a cor, logo e nome da empresa.
// Obs.: o loader de ações (components/Loader) continua sendo o modal de sempre.

const logoCarregando = require("../../img/loader-agenda3.gif");
// react-dom vem com o react-native-web (o projeto não tem @types/react-dom, por isso o require tipado)
const { createPortal } = require("react-dom") as {
  createPortal: (filho: React.ReactNode, destino: Element) => React.ReactPortal;
};

const DOURADO = "#E3BF7A";

interface Props {
  marca?: MarcaAbertura | null;
  texto?: string;
  subtexto?: string;
}

export default function Abertura(props: Props) {
  const conteudo = <ConteudoAbertura {...props} />;
  return typeof document === "undefined" ? conteudo : createPortal(conteudo, document.body);
}

function ConteudoAbertura({
  marca,
  texto = "Carregando",
  subtexto = "Só um instante",
}: Props) {
  if (marca && !marca.legado) {
    return <AberturaEmpresa marca={marca} />;
  }

  return (
    <div style={estilos.tela}>
      <style>{animacoes}</style>

      <div style={estilos.centro}>
        <img src={logoCarregando} alt="Carregando" style={estilos.logo} />

        <div style={estilos.linhaTexto}>
          <span style={estilos.texto}>{texto}</span>
          {renderPontos("#9a958c")}
        </div>

        <span style={estilos.subtexto}>{subtexto}</span>
      </div>

      <span style={estilos.rodape}>ag.sim · sistema de agendamento</span>
    </div>
  );
}

function AberturaEmpresa({ marca }: { marca: MarcaAbertura }) {
  const corTexto = corTextoSobre(marca.cor);
  const [logoComErro, setLogoComErro] = useState(false);

  return (
    <div
      style={{
        ...estilos.tela,
        overflow: "hidden",
        backgroundColor: marca.cor,
        backgroundImage: gradienteHero(marca.cor),
      }}
    >
      <style>{animacoes}</style>

      {/* círculos decorativos */}
      <div style={circulo(300, { top: -90, right: -110 })} />
      <div style={circulo(240, { bottom: 140, left: -150 })} />

      <div style={estilos.centro}>
        <div style={estilos.areaLogo}>
          <div
            className="abertura-arco"
            style={{
              ...estilos.arco,
              borderTopColor: DOURADO,
              borderRightColor: DOURADO,
            }}
          />
          <div style={estilos.anelLogo}>
            <div style={estilos.circuloLogo}>
              {!!marca.logo && !logoComErro ? (
                <img
                  src={marca.logo}
                  alt={marca.nome}
                  style={{ width: "86%", height: "86%", objectFit: "contain" }}
                  onError={() => setLogoComErro(true)}
                />
              ) : (
                <span style={{ ...estilos.inicial, color: marca.cor }}>
                  {(marca.nome || "?").trim().charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          </div>
        </div>

        <span style={{ ...estilos.nomeEmpresa, color: corTexto }}>
          {marca.nome}
        </span>

        <div style={estilos.linhaTexto}>
          <span style={{ ...estilos.textoEmpresa, color: corTexto }}>
            Preparando sua agenda
          </span>
          {renderPontos(corTexto)}
        </div>
      </div>

      <span style={{ ...estilos.rodapeEmpresa, color: corTexto }}>
        Agendamento por <strong style={{ opacity: 1 }}>AG.SIM</strong>
      </span>
    </div>
  );
}

function renderPontos(cor: string) {
  return (
    <span style={estilos.pontos}>
      <span className="abertura-ponto" style={{ background: cor }} />
      <span
        className="abertura-ponto"
        style={{ background: cor, animationDelay: "0.2s" }}
      />
      <span
        className="abertura-ponto"
        style={{ background: cor, animationDelay: "0.4s" }}
      />
    </span>
  );
}

function circulo(
  tamanho: number,
  posicao: React.CSSProperties,
): React.CSSProperties {
  return {
    position: "absolute",
    width: tamanho,
    height: tamanho,
    borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.12)",
    pointerEvents: "none",
    ...posicao,
  };
}

const animacoes = `
  .abertura-ponto {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    display: inline-block;
    animation: abertura-piscar 1.2s infinite ease-in-out;
  }
  @keyframes abertura-piscar {
    0%, 80%, 100% { opacity: 0.25; }
    40% { opacity: 1; }
  }
  .abertura-arco {
    animation: abertura-girar 1.4s linear infinite;
  }
  @keyframes abertura-girar {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const estilos: Record<string, React.CSSProperties> = {
  tela: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    backgroundColor: "#F9F9F7",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "inherit",
  },
  centro: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
  },
  logo: {
    width: 120,
    height: 120,
    marginBottom: 22,
  },
  linhaTexto: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  texto: {
    fontSize: 16,
    fontWeight: 700,
    color: "#1d1d1d",
  },
  pontos: {
    display: "inline-flex",
    gap: 3,
    marginTop: 2,
  },
  subtexto: {
    fontSize: 13,
    color: "#8a857c",
    marginTop: 6,
  },
  rodape: {
    position: "absolute",
    bottom: 28,
    fontSize: 12,
    color: "#a8a39a",
    letterSpacing: 0.3,
  },

  // abertura com a marca da empresa
  areaLogo: {
    position: "relative",
    width: 132,
    height: 132,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 22,
  },
  arco: {
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    border: "2px solid transparent",
  },
  anelLogo: {
    width: 116,
    height: 116,
    borderRadius: "50%",
    padding: 6,
    boxSizing: "border-box",
    backgroundColor: "rgba(255,255,255,0.12)",
  },
  circuloLogo: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    backgroundColor: "#fff",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  inicial: {
    fontSize: 40,
    fontWeight: 800,
  },
  nomeEmpresa: {
    fontSize: 24,
    fontWeight: 800,
    textAlign: "center",
    padding: "0 24px",
    marginBottom: 8,
  },
  textoEmpresa: {
    fontSize: 14,
    fontWeight: 600,
    opacity: 0.9,
  },
  rodapeEmpresa: {
    position: "absolute",
    bottom: 28,
    fontSize: 12,
    opacity: 0.75,
    letterSpacing: 0.3,
  },
};
